// Keeping a dragged product inside the room: the drag frame's clamp
// (productClamping) and the base and upper bounds it is built from.
import * as THREE from 'three';
import {CabinetMesh} from 'components/customer/RoomPlanner/types';
import {store} from 'store/customer';
import {rotatingSet} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {
    applyBulkheadOffset,
    computeWorldBoundsExcludingAnimatedDoorParts,
    getDirection,
    getGroupWidth,
    WALL_ROTATIONS,
} from 'components/customer/RoomPlanner/helpers/calculations';
import {autoRotateClamping} from 'components/customer/RoomPlanner/helpers/boundaries/autoRotate';
import {
    getWallSurfaceBox,
    isGroupBesideWall,
    isWallOccludedByProduct,
    L_SHAPE_ARM_WALL,
    L_SHAPE_LEG_WALL,
} from 'components/customer/RoomPlanner/helpers/wallProximity';
import {isNonRectShapeProduct} from 'components/customer/RoomPlanner/helpers/shapeProducts';

const setRotating = () => {
    const state = store.getState();
    const isRotating = state.roomPlanner.isRotating;

    store.dispatch(rotatingSet(!isRotating));
};

export const productClamping = (
    group: CabinetMesh,
    wallGroup: THREE.Mesh<THREE.ExtrudeGeometry, THREE.MeshStandardMaterial>,
    camera: THREE.Camera,
    mouse: THREE.Vector2,
    newPosition: THREE.Vector3,
    roomType: string,
    isUpperProduct: boolean,
    preventAutoRotation = false, // Flag to prevent auto-rotation during keyboard movement
    products: THREE.Object3D[] = [] // Room products, used to reject walls screened by an existing product
) => {
    const minX = 0;
    const maxX = 0;
    const minZ = 0;
    const maxZ = 0;

    // Placeholders can drag without a resolved wall mesh; return unclamped position.
    if (!wallGroup?.children) {
        return {
            clamped: newPosition.clone(),
            minX: newPosition.x - 5000,
            maxX: newPosition.x + 5000,
            minZ: newPosition.z - 5000,
            maxZ: newPosition.z + 5000,
            wasClamped: false,
        };
    }

    // Calculate wall offset based on direction (will use newDirection if rotation changes)
    const isManualPanel = group.userData.isManualPanel;
    const isFiller =
        group.userData.isFillerProduct ||
        group.userData.isVerminProduct ||
        group.userData.isGhostPanel;
    const wallOffset = isManualPanel ? 30 : 26;
    let wallOffsetX = wallOffset;
    let wallOffsetZ = wallOffset;
    const WallBox = new THREE.Box3();

    wallGroup.children.forEach((child) => {
        if ((child as THREE.Mesh).isMesh) {
            const mesh = child as THREE.Mesh;
            const geometry = mesh.geometry;
            // Check if the geometry has valid position attributes
            if (
                geometry.attributes.position &&
                geometry.attributes.position.count > 0 &&
                !hasNaNPositions(geometry)
            ) {
                geometry.computeBoundingBox();

                if (geometry.boundingBox) {
                    const childBox = geometry.boundingBox
                        .clone()
                        .applyMatrix4(mesh.matrixWorld);
                    WallBox.union(childBox);
                }
            }
        }
    });

    /**
     * Checks if a geometry's position attribute contains any NaN values
     * @param {THREE.BufferGeometry} geometry - The geometry to check
     * @return {boolean} True if NaN values are found, false otherwise
     */
    function hasNaNPositions(geometry: THREE.BufferGeometry): boolean {
        if (!geometry.attributes.position) return false;
        const positions = geometry.attributes.position.array;
        for (let i = 0; i < positions.length; i++) {
            const value = positions[Number(i)];
            if (value !== value) return true;
        }
        return false;
    }

    const {width: latestWidthParam, depth: latestDepthParam} =
        getGroupWidth(group);
    // Placeholders store facing on rotation.y; cabinets use rotation.z.
    const direction = getDirection(
        group.userData?.isPlaceholder ? group.rotation.y : group.rotation.z
    );
    let latestWidth = latestWidthParam;
    let latestDepth = latestDepthParam;

    if (group.userData.isBulkheadComponent) {
        const result = applyBulkheadOffset(
            wallOffsetX,
            wallOffsetZ,
            group,
            direction
        );

        wallOffsetX = result.x;
        wallOffsetZ = result.z;
        latestWidth = latestWidthParam - result.thicknessOffset;
        latestDepth = latestDepthParam - result.thicknessOffset;
    }

    const isNonRectShape = isNonRectShapeProduct(group);

    // Check for wall change and handle auto-rotation BEFORE applying clamping
    // Skip auto-rotation if preventAutoRotation flag is set (e.g., during keyboard movement)
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, camera);
    raycaster.layers.set(0);
    const hits = raycaster.intersectObjects(wallGroup.children);
    const visibleHits = hits.filter((hit) => hit.object.visible);

    let newRotation = group.rotation.z; // Default to current rotation
    let rotationChanged = false;
    let newDirection = direction;
    let newWidth = latestWidth;
    let newDepth = latestDepth;

    if (
        visibleHits.length > 0 &&
        !preventAutoRotation &&
        !isFiller &&
        !group.userData?.isPlaceholder
    ) {
        const wallName = visibleHits[0].object;
        const currentWall = group.userData.currentWall;
        // A null currentWall (e.g. rotated by hand away from any wall) must
        // still be able to adopt the hovered wall.
        if (wallName.name !== currentWall?.name) {
            // The raycast reaches walls the product is nowhere near. Adopting
            // one clamps an asymmetric shape into an L-room cut-out.
            const hoveredWall = wallName as THREE.Mesh;

            // Judge proximity where the cabinet lands this move, not where
            // the previous move left it. Otherwise it reaches the wall on
            // one move and only rotates on the next, so stopping on the
            // wall never rotates it.
            const landing = baseProductClamping(
                group,
                minX,
                maxX,
                minZ,
                maxZ,
                direction,
                wallOffsetX,
                wallOffsetZ,
                latestWidth,
                latestDepth,
                WallBox,
                roomType,
                wallGroup,
                newPosition
            );
            const originalPosition = group.position.clone();
            group.position.set(
                THREE.MathUtils.clamp(
                    newPosition.x,
                    landing.minX,
                    landing.maxX
                ),
                group.position.y,
                THREE.MathUtils.clamp(newPosition.z, landing.minZ, landing.maxZ)
            );
            group.updateMatrixWorld();

            const canAdoptHoveredWall =
                isGroupBesideWall(group, hoveredWall) &&
                !isWallOccludedByProduct(group, hoveredWall, products);

            group.position.copy(originalPosition);
            group.updateMatrixWorld();

            if (wallName.name in WALL_ROTATIONS && canAdoptHoveredWall) {
                newRotation =
                    WALL_ROTATIONS[
                        wallName.name as keyof typeof WALL_ROTATIONS
                    ];
                rotationChanged = true;
                newDirection = getDirection(newRotation);

                // Calculate dimensions for the new rotation by temporarily applying it
                const originalRotation = group.rotation.z;
                group.rotation.z = newRotation;
                group.updateMatrixWorld();

                const rotatedDimensions = getGroupWidth(group);
                newWidth = rotatedDimensions.width;
                newDepth = rotatedDimensions.depth;

                // Restore original rotation temporarily for clamping calculation
                group.rotation.z = originalRotation;
                group.updateMatrixWorld();

                // Update the individual cabinet's currentWall
                group.userData.currentWall = wallName as THREE.Mesh;

                // Update direction
                group.userData.currentDirection =
                    newDirection === 'normal'
                        ? 'front'
                        : newDirection === 'left'
                          ? 'right'
                          : newDirection === 'right'
                            ? 'left'
                            : newDirection;
                setRotating();
            }
        }
    }

    // Rotate before clamping so edge offsets match the final orientation.
    let rotationAppliedPreClamp = false;
    if (rotationChanged && isNonRectShape) {
        const box = new THREE.Box3().setFromObject(group);
        const centerBeforeRotation = box.getCenter(new THREE.Vector3());

        group.rotation.z = newRotation;
        group.updateMatrixWorld();

        const newBox = new THREE.Box3().setFromObject(group);
        const centerAfterRotation = newBox.getCenter(new THREE.Vector3());

        const centerShift = centerAfterRotation
            .clone()
            .sub(centerBeforeRotation);

        group.position.sub(centerShift);

        rotationAppliedPreClamp = true;
    }

    // Recalculate clamping with the new rotation's dimensions and direction
    const finalClampedResult = baseProductClamping(
        group,
        minX,
        maxX,
        minZ,
        maxZ,
        newDirection,
        wallOffsetX,
        wallOffsetZ,
        newWidth,
        newDepth,
        WallBox,
        roomType,
        wallGroup,
        newPosition
    );

    let clamped: THREE.Vector3;
    let wasClamped = false;

    if (rotationChanged && !rotationAppliedPreClamp) {
        const box = new THREE.Box3().setFromObject(group);
        const centerBeforeRotation = box.getCenter(new THREE.Vector3());

        group.rotation.z = newRotation;
        group.updateMatrixWorld();

        const newBox = new THREE.Box3().setFromObject(group);
        const centerAfterRotation = newBox.getCenter(new THREE.Vector3());

        const centerShift = centerAfterRotation
            .clone()
            .sub(centerBeforeRotation);

        group.position.sub(centerShift);
    }

    let upperClampedResult: ReturnType<typeof upperProductClamping> | null =
        null;

    // Placeholders keep the edge-offset clamp from baseProductClamping.
    // Upper corner-origin limits assume the pivot sits on a bbox corner.
    if (isUpperProduct && !isFiller && !group.userData?.isPlaceholder) {
        if (roomType === 'RECTANGULAR') {
            upperClampedResult = upperProductClamping(
                finalClampedResult.minX,
                finalClampedResult.maxX,
                finalClampedResult.minZ,
                finalClampedResult.maxZ,
                newDirection,
                wallOffsetX,
                wallOffsetZ,
                newWidth,
                newDepth,
                WallBox,
                group.userData.currentWall,
                group
            );

            // Remove 'const' to use the outer clamped variable instead of creating a new local one
            clamped = newPosition.clone();

            wasClamped =
                clamped.z < upperClampedResult.minZ ||
                clamped.z > upperClampedResult.maxZ ||
                clamped.x < upperClampedResult.minX ||
                clamped.x > upperClampedResult.maxX;

            clamped.x = THREE.MathUtils.clamp(
                clamped.x,
                upperClampedResult.minX,
                upperClampedResult.maxX
            );
            clamped.z = THREE.MathUtils.clamp(
                clamped.z,
                upperClampedResult.minZ,
                upperClampedResult.maxZ
            );
        } else {
            const autoResult = autoRotateClamping(
                group,
                group.userData.currentWall,
                newPosition,
                isUpperProduct,
                roomType
            );
            clamped = autoResult.clamped;
            wasClamped = !autoResult.clamped.equals(newPosition);
        }
    } else {
        clamped = newPosition.clone();

        wasClamped =
            clamped.z < finalClampedResult.minZ ||
            clamped.z > finalClampedResult.maxZ ||
            clamped.x < finalClampedResult.minX ||
            clamped.x > finalClampedResult.maxX;

        clamped.x = THREE.MathUtils.clamp(
            clamped.x,
            finalClampedResult.minX,
            finalClampedResult.maxX
        );

        clamped.z = THREE.MathUtils.clamp(
            clamped.z,
            finalClampedResult.minZ,
            finalClampedResult.maxZ
        );
    }

    // Placeholders: if clamp bounds/position went NaN, keep the drag position
    if (
        group.userData?.isPlaceholder &&
        (!Number.isFinite(clamped.x) ||
            !Number.isFinite(clamped.y) ||
            !Number.isFinite(clamped.z))
    ) {
        clamped = newPosition.clone();
        wasClamped = false;
    }

    return {
        clamped,
        ...(upperClampedResult ?? finalClampedResult),
        wasClamped,
    };
};

export const upperProductClamping = (
    minX: number,
    maxX: number,
    minZ: number,
    maxZ: number,
    direction: string,
    wallOffsetX: number,
    wallOffsetZ: number,
    latestWidth: number,
    latestDepth: number,
    WallBox: THREE.Box3,
    currentWall: THREE.Mesh | null,
    group?: CabinetMesh
) => {
    if (!currentWall) {
        return {minX, maxX, minZ, maxZ};
    }

    const isNonRectShape = isNonRectShapeProduct(group);

    if (isNonRectShape) {
        return {minX, maxX, minZ, maxZ};
    }

    switch (currentWall.name) {
        case 'wall:back':
            if (direction === 'normal') {
                maxZ = WallBox.min.z + latestDepth - 4 + wallOffsetZ;
            }
            if (direction === 'back') {
                maxZ = WallBox.min.z + 5 + wallOffsetZ;
            }
            if (direction === 'right') {
                maxZ = WallBox.min.z + latestDepth + wallOffsetZ + 2;
            }
            if (direction === 'left') {
                maxZ = WallBox.min.z + wallOffsetZ + 2;
            }
            break;
        case 'wall:front':
            if (direction === 'normal') {
                minZ = WallBox.max.z - 5 - wallOffsetZ;
            }
            if (direction === 'back') {
                minZ = WallBox.max.z - latestDepth + 4 - wallOffsetZ;
            }
            if (direction === 'right') {
                minZ = WallBox.max.z - wallOffsetZ;
            }
            if (direction === 'left') {
                minZ = WallBox.max.z - latestDepth - wallOffsetZ;
            }
            break;
        case 'wall:left':
            if (direction === 'normal') {
                maxX = WallBox.min.x + wallOffsetX - 1;
                minX = WallBox.min.x + wallOffsetX - 1;
            }
            if (direction === 'back') {
                maxX = WallBox.min.x + latestWidth + wallOffsetX;
            }
            if (direction === 'right') {
                maxX = WallBox.min.x + latestWidth - 4 + wallOffsetX;
            }
            if (direction === 'left') {
                maxX = WallBox.min.x + 5 + wallOffsetX;
            }
            break;
        case 'wall:right':
            if (direction === 'normal') {
                minX = WallBox.max.x - latestWidth - wallOffsetX;
            }
            if (direction === 'back') {
                minX = WallBox.max.x - wallOffsetX;
            }
            if (direction === 'right') {
                minX = WallBox.max.x - 5 - wallOffsetX;
            }
            if (direction === 'left') {
                minX = WallBox.max.x - latestWidth + 2 - wallOffsetX;
            }
            break;
    }

    return {minX, maxX, minZ, maxZ};
};

export const baseProductClamping = (
    group: CabinetMesh,
    minX: number,
    maxX: number,
    minZ: number,
    maxZ: number,
    direction: string,
    wallOffsetX: number,
    wallOffsetZ: number,
    latestWidth: number,
    latestDepth: number,
    WallBox: THREE.Box3,
    roomType: string,
    wallGroup: THREE.Mesh<THREE.ExtrudeGeometry, THREE.MeshStandardMaterial>,
    newPosition: THREE.Vector3
) => {
    const defaultStaticOffset = 1;
    const isFillerProduct = group.userData.isFillerProduct;
    const isVerminProduct = group.userData.isVerminProduct;
    const isManualPanel = group.userData.isManualPanel;
    const isPlaceholder = Boolean(group.userData?.isPlaceholder);
    const isNonRectShape = isNonRectShapeProduct(group);
    // Non-rect panels and placeholders both have pivots that are not at a
    // bbox corner (GLB pivot for placeholders). Clamp from real AABB edges.
    const useEdgeOffsets = isNonRectShape || isPlaceholder;

    // Position-to-AABB-edge offsets. For non-rectangular shapes / placeholders
    // the origin is not at a bbox corner, so clamping must be expressed in
    // terms of the actual edges. The L-shape inner-wall block below also
    // reuses these offsets so the same edge-based reasoning applies on the
    // notch. Placeholders use the mesh-only world bounds helper so overlay
    // children (arrows, measurements) don't inflate the clamp footprint.
    const groupBox = isPlaceholder
        ? computeWorldBoundsExcludingAnimatedDoorParts(group)
        : new THREE.Box3().setFromObject(group);
    const offsetToMinX = group.position.x - groupBox.min.x;
    const offsetToMaxX = groupBox.max.x - group.position.x;
    const offsetToMinZ = group.position.z - groupBox.min.z;
    const offsetToMaxZ = groupBox.max.z - group.position.z;

    if (useEdgeOffsets) {
        minX = WallBox.min.x + offsetToMinX + wallOffsetX - defaultStaticOffset;
        maxX = WallBox.max.x - offsetToMaxX - wallOffsetX + defaultStaticOffset;
        minZ = WallBox.min.z + offsetToMinZ + wallOffsetZ - defaultStaticOffset;
        maxZ = WallBox.max.z - offsetToMaxZ - wallOffsetZ + defaultStaticOffset;
    } else {
        const carcassThickness =
            Number(group.userData.values?.cabinet_carc_thickness) || 0;

        const selectedCabinetDepth =
            isFillerProduct || isManualPanel
                ? latestDepth - carcassThickness * 0.2
                : isVerminProduct
                  ? Number(group.userData.depth) - carcassThickness * 0.2
                  : Number(group.userData.depth) || latestDepth;
        const cabinetDepthOffset =
            isFillerProduct || isManualPanel
                ? 0
                : selectedCabinetDepth - latestDepth;
        const cabinetDepthOffsetSide =
            isFillerProduct || isManualPanel
                ? 0
                : selectedCabinetDepth - latestWidth;
        const panelWidthOffset = group.userData.widthOffset;
        if (direction === 'normal') {
            minZ =
                WallBox.min.z +
                selectedCabinetDepth -
                defaultStaticOffset +
                wallOffsetZ;
            maxZ =
                WallBox.max.z +
                defaultStaticOffset -
                wallOffsetZ +
                cabinetDepthOffset;
            minX =
                WallBox.min.x +
                wallOffsetX -
                defaultStaticOffset +
                panelWidthOffset;
            maxX =
                WallBox.max.x -
                latestWidth -
                wallOffsetX +
                defaultStaticOffset +
                panelWidthOffset;
        } else if (direction === 'back') {
            minZ =
                WallBox.min.z -
                defaultStaticOffset +
                wallOffsetZ -
                cabinetDepthOffset;
            maxZ =
                WallBox.max.z -
                selectedCabinetDepth +
                defaultStaticOffset -
                wallOffsetZ;

            minX =
                WallBox.min.x +
                latestWidth +
                wallOffsetX -
                defaultStaticOffset -
                panelWidthOffset;
            maxX =
                WallBox.max.x -
                wallOffsetX +
                defaultStaticOffset -
                panelWidthOffset;
        } else if (direction === 'right') {
            minZ =
                WallBox.min.z +
                latestDepth +
                wallOffsetZ -
                defaultStaticOffset -
                panelWidthOffset;
            maxZ =
                WallBox.max.z -
                wallOffsetZ +
                defaultStaticOffset -
                panelWidthOffset;

            minX =
                WallBox.min.x +
                selectedCabinetDepth -
                defaultStaticOffset +
                wallOffsetX;
            maxX =
                WallBox.max.x -
                wallOffsetX +
                cabinetDepthOffsetSide +
                defaultStaticOffset;
        } else if (direction === 'left') {
            minZ =
                WallBox.min.z +
                wallOffsetZ -
                defaultStaticOffset +
                panelWidthOffset;
            maxZ =
                WallBox.max.z -
                latestDepth -
                wallOffsetZ +
                defaultStaticOffset +
                panelWidthOffset;

            minX =
                WallBox.min.x +
                wallOffsetX -
                cabinetDepthOffsetSide -
                defaultStaticOffset;
            maxX =
                WallBox.max.x -
                selectedCabinetDepth +
                defaultStaticOffset -
                wallOffsetX;
        }
    }

    if (roomType === 'LSHAPED') {
        const leftWidth2Wall = (
            wallGroup.parent as THREE.Scene
        ).getObjectByName('leftWidth2Wall') as THREE.Mesh;
        const rightWidth2Wall = (
            wallGroup.parent as THREE.Scene
        ).getObjectByName('rightWidth2Wall') as THREE.Mesh;
        // Use geometry-derived bboxes; `setFromObject` would include each
        // wall's dimension-arrow children and inflate the AABB past the
        // actual wall surface, which breaks the corner-penetration test
        // below for L/Key/U panels.
        const leftWidth2Wallbox = getWallSurfaceBox(leftWidth2Wall);
        const rightWidth2Wallbox = getWallSurfaceBox(rightWidth2Wall);

        const projectedMaxX = newPosition.x + offsetToMaxX;
        const projectedMaxZ = newPosition.z + offsetToMaxZ;

        if (useEdgeOffsets) {
            const leftWallFaceX = leftWidth2Wallbox.min.x;
            const rightWallFaceZ = rightWidth2Wallbox.min.z;

            if (
                projectedMaxX > leftWallFaceX &&
                projectedMaxZ > rightWallFaceZ
            ) {
                const penetrationX = projectedMaxX - leftWallFaceX;
                const penetrationZ = projectedMaxZ - rightWallFaceZ;

                if (penetrationX > penetrationZ) {
                    const targetZ = rightWallFaceZ - offsetToMaxZ;
                    if (targetZ < maxZ) maxZ = targetZ;
                } else {
                    const targetX = leftWallFaceX - offsetToMaxX;
                    if (targetX < maxX) maxX = targetX;
                }
            } else if (projectedMaxX > leftWallFaceX) {
                const targetZ = rightWallFaceZ - offsetToMaxZ;
                if (targetZ < maxZ) maxZ = targetZ;
            } else if (projectedMaxZ > rightWallFaceZ) {
                const targetX = leftWallFaceX - offsetToMaxX;
                if (targetX < maxX) maxX = targetX;
            }
        }

        // CO-4235 + Deo's CO-5034 overlap fix, reconciled by scenario:
        //  - hovering an inner L-wall  -> CO-4235 flush-snap limits (wall-attach fix)
        //  - free-dragging the corner  -> Deo's geometry overlap clamp, but gated by
        //    real penetration so it no longer fires unconditionally (that unconditional
        //    pin-clamp is what CO-4929's deletion was working around).
        // Tuning knob: if wall-attach flush ever regresses, the hover branches own that;
        // if drag overlap regresses, the floor branch (Deo's targets/gate) owns that.
        if (!useEdgeOffsets) {
            const hoveredWall = group.userData.currentWall?.name;
            const besideInnerWall =
                !!group.userData.currentWall &&
                isGroupBesideWall(group, group.userData.currentWall);
            const selectedCabinetDepth =
                isFillerProduct || isManualPanel
                    ? latestDepth
                    : Number(group.userData.depth) || latestDepth;
            const cabinetDepthOffset =
                isFillerProduct || isManualPanel
                    ? 0
                    : selectedCabinetDepth - latestDepth;
            const cabinetDepthOffsetSide =
                isFillerProduct || isManualPanel
                    ? 0
                    : selectedCabinetDepth - latestWidth;
            const panelWidthOffset = group.userData.widthOffset;

            // CO-4235 flush-snap targets (0-gap wall alignment) for the hover case.
            let limitMaxX = maxX;
            let limitMaxZ = maxZ;

            if (direction === 'normal') {
                limitMaxX =
                    leftWidth2Wallbox.min.x - latestWidth + panelWidthOffset;
                limitMaxZ = rightWidth2Wallbox.min.z + cabinetDepthOffset;
            } else if (direction === 'back') {
                limitMaxX = leftWidth2Wallbox.min.x - panelWidthOffset;
                limitMaxZ = rightWidth2Wallbox.min.z - selectedCabinetDepth;
            } else if (direction === 'right') {
                limitMaxX = leftWidth2Wallbox.min.x + cabinetDepthOffsetSide;
                limitMaxZ = rightWidth2Wallbox.min.z - panelWidthOffset;
            } else if (direction === 'left') {
                limitMaxX = leftWidth2Wallbox.min.x - selectedCabinetDepth;
                limitMaxZ =
                    rightWidth2Wallbox.min.z - latestDepth + panelWidthOffset;
            }

            let cabRightEdge = newPosition.x;
            let cabFrontEdge = newPosition.z;

            if (direction === 'normal') {
                cabRightEdge += latestWidth - panelWidthOffset;
                cabFrontEdge -= cabinetDepthOffset;
            } else if (direction === 'back') {
                cabRightEdge += panelWidthOffset;
                cabFrontEdge += selectedCabinetDepth;
            } else if (direction === 'right') {
                cabRightEdge -= cabinetDepthOffsetSide;
                cabFrontEdge += panelWidthOffset;
            } else if (direction === 'left') {
                cabRightEdge += selectedCabinetDepth;
                cabFrontEdge += latestDepth - panelWidthOffset;
            }

            const leftWallFaceX = leftWidth2Wallbox.min.x;
            const rightWallFaceZ = rightWidth2Wallbox.min.z;

            if (
                besideInnerWall &&
                hoveredWall === 'leftWidth2Wall' &&
                cabFrontEdge > rightWallFaceZ
            ) {
                if (limitMaxX < maxX) maxX = limitMaxX;
            } else if (
                besideInnerWall &&
                hoveredWall === 'rightWidth2Wall' &&
                cabRightEdge > leftWallFaceX
            ) {
                if (limitMaxZ < maxZ) maxZ = limitMaxZ;
            } else {
                // Floor drag: cross-axis notch clamp. When only one edge crosses a
                // notch wall face, limit the OTHER axis so the cabinet can still
                // slide into the right/bottom leg; clamp both only when it would
                // fill the reentrant corner.
                if (
                    cabRightEdge > leftWallFaceX &&
                    cabFrontEdge > rightWallFaceZ
                ) {
                    const penetrationX = cabRightEdge - leftWallFaceX;
                    const penetrationZ = cabFrontEdge - rightWallFaceZ;
                    if (penetrationX > penetrationZ) {
                        if (limitMaxZ < maxZ) maxZ = limitMaxZ;
                    } else {
                        if (limitMaxX < maxX) maxX = limitMaxX;
                    }
                } else if (cabRightEdge > leftWallFaceX) {
                    if (limitMaxZ < maxZ) maxZ = limitMaxZ;
                } else if (cabFrontEdge > rightWallFaceZ) {
                    if (limitMaxX < maxX) maxX = limitMaxX;
                }
            }
        }

        const upperWallName = group.userData.currentWall?.name;

        if (group.userData.isUpperProduct) {
            if (upperWallName === L_SHAPE_LEG_WALL) {
                const spanMinZ = leftWidth2Wallbox.min.z + offsetToMinZ;
                const spanMaxZ = leftWidth2Wallbox.max.z - offsetToMaxZ;
                if (spanMinZ <= spanMaxZ) {
                    minZ = Math.max(minZ, spanMinZ);
                    maxZ = Math.min(maxZ, spanMaxZ);
                }
            } else if (upperWallName === L_SHAPE_ARM_WALL) {
                const spanMinX = rightWidth2Wallbox.min.x + offsetToMinX;
                const spanMaxX = rightWidth2Wallbox.max.x - offsetToMaxX;
                if (spanMinX <= spanMaxX) {
                    minX = Math.max(minX, spanMinX);
                    maxX = Math.min(maxX, spanMaxX);
                }
            }
        }
    }

    return {minX, maxX, minZ, maxZ};
};
